import React, { useEffect, useState } from "react";
import Sidebar from "../../components/Layout/Sidebar";
import Header from "../../components/Layout/Header";
import Pagination from "../../components/Common/Pagination";
import Toast from "../../components/Common/Toast";
import CampaignProgressModal from "../../components/Common/CampaignProgressModal";
import "../../assets/styles/mainpage.css";
import "../../assets/styles/adspower-manager.css";
import "../../assets/styles/campaign-list.css";
import commonService from "../../services/otherServices";
import { getErrorMessage } from "../../services/apiClient";
import { formatIST } from "../../utils/dateHelpers";
import { useNavigate } from "react-router-dom";
import {
  Layers3,
  CheckCircle2,
  Activity,
  Clock3,
  Inbox,
  Eye,
  Code2,
  Download,
  X
} from "lucide-react";

const CampaignListPage: React.FC = () => {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState(1);
  const [pageSize] = useState(5);
  const [totalPages, setTotalPages] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);

  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");

  const [summary, setSummary] = useState<any>(null);

  const [toast, setToast] = useState<{ type: "success" | "error"; message: string } | null>(null);

  const [progressPanel, setProgressPanel] = useState<any | null>(null);

  const [htmlPanel, setHtmlPanel] = useState<{ campaign: any; files: any[] } | null>(null);

  const [htmlLoading, setHtmlLoading] = useState(false);

  const [downloadingHtmlName, setDownloadingHtmlName] = useState<string | null>(null);
  
  const [viewingHtmlName, setViewingHtmlName] = useState<string | null>(null);


  const fetchCampaigns = async () => {
    try {
      setLoading(true);

      const res = await commonService.campaignList({
        page,
        limit: pageSize,
        search,
      });

      setCampaigns(res.data.data || []);
      setTotalPages(res.data.total_pages || 0);
      setTotalRecords(res.data.total || 0);

    } catch (error) {
      setToast({ type: "error", message: getErrorMessage(error, "Failed to fetch campaigns") });
    } finally {
      setLoading(false);
    }
  };

  const fetchSummary = async () => {
    try {
      const res = await commonService.dashboard({});
      setSummary(res.data?.campaign_summary || null);
    } catch (error) {
      // Non-critical: the list still works without the summary strip.
    }
  };

  useEffect(() => {
    fetchCampaigns();
  }, [page, search]);

  useEffect(() => {
    fetchSummary();
  }, []);

  const handleSearch = () => {
    setPage(1);
    setSearch(searchInput);
  };

  const onChangeAction = (item: any, action: string) => {
    if (action === "Launch") {
      navigate(`/campaign-review/${item.id}`);
    } else if (action === "details") {
      navigate(`/campaign-details/${item.id}`);
    } else if (action === "strategy") {
      navigate(`/campaign-strategy/${item.id}`);
    }
  }

  const openHtmlFiles = async (campaign: any) => {
    setHtmlLoading(true);
    try {
      const res = await commonService.listCampaignRawHtml(campaign.id);
      setHtmlPanel({ campaign, files: res.data?.files || [] });
    } catch (error) {
      setToast({ type: "error", message: getErrorMessage(error, "Failed to load saved HTML files") });
    } finally {
      setHtmlLoading(false);
    }
  };

  const handleDownloadHtml = async (campaignId: number, filename: string) => {
    setDownloadingHtmlName(filename);
    try {
      await commonService.downloadCampaignRawHtml(campaignId, filename);
    } catch (error) {
      setToast({ type: "error", message: getErrorMessage(error, "Failed to download HTML file") });
    } finally {
      setDownloadingHtmlName(null);
    }
  };

  const handleViewHtml = async (campaignId: number, filename: string) => {
    setViewingHtmlName(filename);
    try {
      await commonService.viewCampaignRawHtml(campaignId, filename);
    } catch (error) {
      setToast({ type: "error", message: getErrorMessage(error, "Failed to open HTML file") });
    } finally {
      setViewingHtmlName(null);
    }
  };

  const formatStatus = (status: string) => {
    if (!status) return "";

    return status
      .toLowerCase()
      .replace(/_/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());
  };

  return (
    <div className="app-container">
      <Sidebar />

      <div className="main">
        <Header />

        <div className="content">
          {/* Header Row */}
          <div style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "20px"
          }}>
            <h2 className="campaign-list-title">Campaign List</h2>

            {/* 🔍 Search Right */}
            <div style={{ display: "flex", gap: "10px" }}>
              <input
                type="text"
                placeholder="Search campaign..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="campaign-search-input"
              />

              <button
                onClick={handleSearch}
                style={{
                  padding: "10px 20px",
                  fontSize: "14px",
                  fontWeight: 600,
                  borderRadius: "10px",
                  background: "#2563eb",
                  color: "#fff",
                  border: "none",
                  cursor: "pointer",
                  transition: "background 0.15s ease"
                }}
              >
                Search
              </button>
            </div>
          </div>

          {/* Status Summary */}
          {summary && (
            <div className="stat-grid">

              <div className="stat-card">
                <div className="stat-icon stat-icon-blue">
                  <Layers3 size={20} />
                </div>
                <div>
                  <strong>{summary.total_campaigns || 0}</strong>
                  <span>Total Campaigns</span>
                </div>
              </div>

              <div className="stat-card">
                <div className="stat-icon stat-icon-green">
                  <CheckCircle2 size={20} />
                </div>
                <div>
                  <strong>{summary.ready || 0}</strong>
                  <span>Ready</span>
                </div>
              </div>

              <div className="stat-card">
                <div className="stat-icon stat-icon-amber">
                  <Activity size={20} />
                </div>
                <div>
                  <strong>{summary.inprogress || 0}</strong>
                  <span>In Progress</span>
                </div>
              </div>

              <div className="stat-card">
                <div className="stat-icon stat-icon-red">
                  <Clock3 size={20} />
                </div>
                <div>
                  <strong>{summary.open || 0}</strong>
                  <span>Pending</span>
                </div>
              </div>

            </div>
          )}

          {/* Table Card */}
          <div
            className="upload-card"
            style={{
              width: "100%",
              padding: "0",
            }}
          >
            {loading ? (
              <div className="loading-box">
                Loading campaigns...
              </div>
            ) : campaigns.length === 0 ? (
              <div className="empty-box">
                <Inbox size={30} />
                <strong>No campaigns found</strong>
                <span>
                  {
                    search
                      ? "Try a different search term."
                      : "Upload target domains to create your first campaign."
                  }
                </span>
              </div>
            ) : (
              <table
                style={{
                  width: "100%",
                  borderCollapse: "separate",
                  borderSpacing: 0
                }}
              >
                <thead className="campaign-table-head">
                  <tr>
                    <th className="campaign-table-th">#</th>
                    {/* <th className="campaign-table-th">Campaign ID</th> */}
                    <th className="campaign-table-th">File Name</th>
                    <th className="campaign-table-th">Data Count</th>
                    <th className="campaign-table-th">Status</th>
                    <th className="campaign-table-th">Created At</th>
                    <th className="campaign-table-th">Email Summary</th>
                    <th className="campaign-table-th">Progress</th>
                    <th className="campaign-table-th">Action</th>
                  </tr>
                </thead>

                <tbody>
                  {campaigns.map((item, index) => (
                    <tr
                      key={item.id}
                      className="campaign-table-row"
                    >
                      {/* ✅ Serial Number */}
                      <td className="campaign-table-td">
                        {(page - 1) * pageSize + index + 1}
                      </td>

                      {/* <td className="campaign-table-td">{item.campaign_id || "-"}</td> */}
                      <td className="campaign-table-td">
                        <div
                          title={item.file_name}
                          style={{
                            maxWidth: "180px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            fontWeight: 500
                          }}
                        >
                          {item.file_name}
                        </div>
                      </td>
                      <td className="campaign-table-td">{item.data_count}</td>

                      {/* ✅ Status Badge */}
                      <td className="campaign-table-td">
                        <div
                          className="status-tooltip-wrapper"
                        >
                          {/* Badge */}
                          <span
                            style={{
                              padding: "4px 10px",
                              borderRadius: "999px",
                              fontSize: "11px",
                              fontWeight: 700,
                              textTransform: "uppercase",

                              background:
                                item.status === "ready"
                                  ? "#dcfce7"
                                  : item.status === "open"
                                    ? "#fee2e2"
                                    : item.status === "inprogress"
                                      ? "#dbeafe"
                                      : "#fef3c7",

                              color:
                                item.status === "ready"
                                  ? "#166534"
                                  : item.status === "open"
                                    ? "#991b1b"
                                    : item.status === "inprogress"
                                      ? "#1d4ed8"
                                      : "#92400e",

                              cursor: "pointer"
                            }}
                          >
                            {
                              formatStatus(item.status)
                            }
                          </span>

                          {/* HOVER CARD */}
                          <div className="status-tooltip">

                            <div className="status-main">
                              <div className="status-main-title">
                                Readiness
                              </div>

                              <div
                                className={`status-pill ${item.processing_status?.campaign_status
                                  }`}
                              >
                                {
                                  formatStatus(
                                    item.processing_status?.campaign_status
                                  )
                                }
                              </div>
                            </div>

                            <div className="status-grid">

                              <div className="status-box">
                                <span className="status-label">
                                  Sequence
                                </span>

                                <span className="status-value">
                                  {
                                    item.processing_status?.sequence?.status
                                  }
                                </span>
                              </div>

                              <div className="status-box">
                                <span className="status-label">
                                  Scraping
                                </span>

                                <span className="status-value">
                                  {
                                    item.processing_status?.scraping?.completed_count
                                  }
                                  /
                                  {
                                    item.processing_status?.scraping?.total_count
                                  }
                                </span>
                              </div>

                              <div className="status-box">
                                <span className="status-label">
                                  Verification
                                </span>

                                <span className="status-value">
                                  {
                                    item.processing_status?.verification?.completed_count
                                  }
                                  /
                                  {
                                    item.processing_status?.verification?.total_count
                                  }
                                </span>
                              </div>
                            </div>

                            {
                              item.processing_status?.pending_reason?.length > 0 && (
                                <div className="pending-box">
                                  ⚠️ {
                                    item.processing_status.pending_reason
                                      .map((r: string) =>
                                        formatStatus(r)
                                      )
                                      .join(", ")
                                  }
                                </div>
                              )
                            }

                          </div>
                        </div>
                      </td>
                      <td className="campaign-table-td">
                        {formatIST(item.created_at, {
                          dateStyle: "medium",
                          timeStyle: "short"
                        })}
                      </td>
                      <td className="campaign-table-td">
                        <div
                          style={{
                            position: "relative",
                            display: "inline-block"
                          }}
                          className="email-summary-wrapper"
                        >
                          {/* Compact Display */}
                          <div
                            style={{
                              padding: "6px 12px",
                              borderRadius: "999px",
                              background: "#eef2ff",
                              color: "#4338ca",
                              fontSize: "12px",
                              fontWeight: 600,
                              cursor: "pointer",
                              whiteSpace: "nowrap"
                            }}
                          >
                            📧 {item.email_summary?.total_emails || 0} Emails
                          </div>

                          {/* Hover Tooltip */}
                          <div className="email-summary-tooltip">
                            <div className="tooltip-row valid">
                              <span>✅ Valid</span>
                              <strong>{item.email_summary?.valid_emails || 0}</strong>
                            </div>

                            <div className="tooltip-row invalid">
                              <span>❌ Invalid</span>
                              <strong>{item.email_summary?.invalid_emails || 0}</strong>
                            </div>

                            <div className="tooltip-row catch">
                              <span>📭 Catch-All</span>
                              <strong>{item.email_summary?.catch_all_emails || 0}</strong>
                            </div>

                            <div className="tooltip-row risky">
                              <span>⚠️ Risky</span>
                              <strong>{item.email_summary?.risky_emails || 0}</strong>
                            </div>
                          </div>
                        </div>
                      </td>

                      <td>
                        <button
                          onClick={() => setProgressPanel(item)}
                          className="progress-btn"
                        >
                          <Activity size={13} />
                          Progress
                        </button>
                      </td>

                      <td>
                        <button
                          onClick={() => onChangeAction(item,"details")}
                          className="progress-btn"
                        >
                          <Eye size={13} />
                          View Detail
                        </button>
                        <button
                          onClick={() => openHtmlFiles(item)}
                          className="progress-btn"
                          style={{ marginLeft: "6px" }}
                        >
                          <Code2 size={13} />
                          HTML
                        </button>
                        {/* <select
                          className="vdc-action-select"
                          onChange={(e) => {
                            if (e.target.value !== "") {
                              onChangeAction(item, e.target.value);
                              e.target.value = "";
                            }
                          }}
                        >
                          <option value="">⚙️ Action</option>

                          {item.status !== "open" ? (
                            <>
                              <option value="Launch">
                                📋 Review &amp; Launch
                              </option>

                              <option value="details">
                                📄 View Details
                              </option>
                            </>
                          ) : (
                            <>
                              <option value="details">
                                📄 View Details
                              </option>

                              <option value="strategy">
                                📑 Define Campaign Strategy
                              </option>
                            </>
                          )}
                        </select> */}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Pagination */}
          <div style={{ marginTop: "15px" }}>
            <Pagination
              page={page}
              total_pages={totalPages}
              total_records={totalRecords}
              page_size={pageSize}
              onPageChange={(p: number) => setPage(p)}
            />
          </div>
        </div>
      </div>

      {progressPanel && (
        <CampaignProgressModal campaign={progressPanel} onClose={() => setProgressPanel(null)} />
      )}

      {/* SAVED HTML PANEL */}
      {(htmlPanel || htmlLoading) && (
        <div
          style={{
            position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)",
            zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center",
            padding: "20px", backdropFilter: "blur(4px)"
          }}
        >
          <div style={{ width: "100%", maxWidth: "620px", background: "#fff", borderRadius: "18px", overflow: "hidden", maxHeight: "82vh", display: "flex", flexDirection: "column" }}>
            <div style={{ padding: "18px 22px", borderBottom: "1px solid #e5e7eb", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <h3 style={{ margin: 0, fontSize: "17px", fontWeight: 700 }}>
                  {htmlPanel ? `${htmlPanel.campaign.file_name} — Campaign #${htmlPanel.campaign.id}` : "Loading..."}
                </h3>
                <div style={{ fontSize: "12px", color: "#6b7280", marginTop: "4px" }}>
                  Saved Google result-page HTML, per scraped domain
                </div>
              </div>
              <button
                onClick={() => setHtmlPanel(null)}
                style={{ border: "none", background: "transparent", cursor: "pointer", color: "#6b7280" }}
              >
                <X size={18} />
              </button>
            </div>

            <div style={{ overflowY: "auto", flex: 1 }}>
              {htmlLoading ? (
                <div style={{ padding: "40px", textAlign: "center", color: "#9ca3af" }}>Loading saved HTML files...</div>
              ) : !htmlPanel || htmlPanel.files.length === 0 ? (
                <div style={{ padding: "40px", textAlign: "center", color: "#9ca3af" }}>
                  No saved HTML files found for this campaign
                </div>
              ) : (
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      <th style={htmlModalThStyle}>Domain</th>
                      <th style={{ ...htmlModalThStyle, textAlign: "center" }}>Pages</th>
                      <th style={htmlModalThStyle}>Saved</th>
                      <th style={htmlModalThStyle}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {htmlPanel.files.map((file) => (
                      <tr key={file.filename}>
                        <td style={htmlModalTdStyle}>{file.domain}</td>
                        <td style={{ ...htmlModalTdStyle, textAlign: "center" }}>{file.page_count ?? "-"}</td>
                        <td style={htmlModalTdStyle}>{file.modified_at ? formatIST(file.modified_at) : "-"}</td>
                        <td style={htmlModalTdStyle}>
                          <div style={{ display: "flex", gap: "6px" }}>
                            <button
                              onClick={() => handleViewHtml(htmlPanel.campaign.id, file.filename)}
                              disabled={viewingHtmlName === file.filename}
                              style={{ ...htmlModalIconTextBtn, opacity: viewingHtmlName === file.filename ? 0.5 : 1 }}
                            >
                              <Eye size={13} />
                              {viewingHtmlName === file.filename ? "..." : "View"}
                            </button>
                            <button
                              onClick={() => handleDownloadHtml(htmlPanel.campaign.id, file.filename)}
                              disabled={downloadingHtmlName === file.filename}
                              style={{ ...htmlModalDownloadBtn, opacity: downloadingHtmlName === file.filename ? 0.5 : 1 }}
                            >
                              <Download size={13} />
                              {downloadingHtmlName === file.filename ? "..." : "Download"}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <Toast
          type={toast.type}
          message={toast.message}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

// Below: exact copies of AdminCampaignsPage.tsx's thStyle/tdStyle/iconTextBtn/
// downloadBtn, scoped to this file's own HTML modal only, so the user-facing
// modal is visually identical to Admin's (same header caps/spacing, same
// button sizing/colors) without depending on or modifying the Admin file.
const htmlModalThStyle: React.CSSProperties = {
  textAlign: "left",
  padding: "12px 14px",
  fontSize: "11px",
  color: "#6b7280",
  textTransform: "uppercase",
  letterSpacing: "0.5px",
  whiteSpace: "nowrap",
  borderBottom: "1px solid #e5e7eb",
};

const htmlModalTdStyle: React.CSSProperties = {
  padding: "13px 14px",
  fontSize: "13px",
  color: "#374151",
  borderBottom: "1px solid #f3f4f6",
};

const htmlModalIconTextBtn: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: "6px",
  border: "1px solid #e5e7eb",
  background: "#fff",
  color: "#374151",
  borderRadius: "8px",
  padding: "6px 10px",
  fontSize: "12px",
  fontWeight: 600,
  cursor: "pointer",
};

const htmlModalDownloadBtn: React.CSSProperties = {
  ...htmlModalIconTextBtn,
  border: "none",
  background: "#2563eb",
  color: "#fff",
};

export default CampaignListPage;